import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAssemblyType } from "./enums/enumAssemblyType.generated";

// Model Objects

export class RecipeIngredientLineReadOnly extends ModelObject<RecipeIngredientLineReadOnly> {

    /**
    * Recipe to which this Ingredient belongs.
    */
    public recipeId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.recipeId = Guid.parseOrEmpty(jsonSerializedObject.recipeId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.recipeId = this.recipeId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "recipeId", rule: (v: any) => !v.isEmpty() || "RecipeIngredientLineReadOnly.RecipeId cannot be empty"},
    ];
}


export class RecipeIngredientLine extends ModelObjectWithDirtyCheck<RecipeIngredientLine> {

    /**
    * Quantity of ingredient required.
    * Greater Than : 0
    */
    public quantity: number = 0;
    /**
    * Sequence (for ordering Lines).
    */
    public sequence: number = 0;
    /**
    * Stock Item Id of the material being used.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Unit of Measure
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    /**
    * Whether this ingredient is fixed to its current quantity regardless of the planned quantity of the assemblies using this recipe.If set to true, the ingredient quantity will not be multiplied when adjusting planned assembly output quantities.
    */
    public fixedQuantity: boolean = false;
    /**
    * Notes.
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    public id: Guid = Guid.createEmpty();
    public readOnly: RecipeIngredientLineReadOnly = new RecipeIngredientLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.quantity = jsonSerializedObject.quantity;
          this.sequence = jsonSerializedObject.sequence;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.fixedQuantity = jsonSerializedObject.fixedQuantity;
          this.notes = jsonSerializedObject.notes;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.readOnly = new RecipeIngredientLineReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.quantity = this.quantity;
        toRet.sequence = this.sequence;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.fixedQuantity = this.fixedQuantity;
        toRet.notes = this.notes;
        toRet.id = this.id.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "quantity", rule: (v: any) => v == null || v > 0 || "Quantity is not greater than minimum allowed value: 0"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "RecipeIngredientLine.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "RecipeIngredientLine.UnitOfMeasureId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "RecipeIngredientLine.Id cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class RecipeOutput extends ModelObject<RecipeOutput> {

    public quantity: number = 0;
    public stockItemId: Guid = Guid.createEmpty();
    public unitOfMeasureId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.quantity = jsonSerializedObject.quantity;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.quantity = this.quantity;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "RecipeOutput.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "RecipeOutput.UnitOfMeasureId cannot be empty"},
    ];
}


export class RecipeHeader extends ModelObject<RecipeHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Assembly Type made from this recipe. : 0 = MaterialProduction, 1 = ProductPackaging, 2 = Maintenance
    */
    public assemblyType: EnumAssemblyType = EnumAssemblyType.materialProduction;
    /**
    * Name.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Notes.
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    private _bestBeforeDaysFromStart: number | null = null;
    /**
    * Best Before Days From Start
    */
    public get bestBeforeDaysFromStart(): number | null { return this._bestBeforeDaysFromStart }
    public set bestBeforeDaysFromStart(value: number | null) {
      this._bestBeforeDaysFromStart = value === undefined ? null : value;
    }
    private _output: RecipeOutput | null = null;
    public get output(): RecipeOutput | null { return this._output }
    public set output(value: RecipeOutput | null) {
      this._output = value === undefined ? null : value;
    }
    public isHidden: boolean = false;
    /**
    * Vessel Fill Colour
    * Not Nullable
    */
    public vesselFillColour: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.assemblyType = EnumAssemblyType.getByValue(jsonSerializedObject.assemblyType, this.assemblyType);
          this.name = jsonSerializedObject.name;
          this.notes = jsonSerializedObject.notes;
          this.bestBeforeDaysFromStart = jsonSerializedObject.bestBeforeDaysFromStart == null ? null : jsonSerializedObject.bestBeforeDaysFromStart;
          this.output = jsonSerializedObject.output == null ? null : new RecipeOutput(jsonSerializedObject.output);
          this.isHidden = jsonSerializedObject.isHidden;
          this.vesselFillColour = jsonSerializedObject.vesselFillColour;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.assemblyType = this.assemblyType.value;
        toRet.name = this.name;
        toRet.notes = this.notes;
        toRet.bestBeforeDaysFromStart = this.bestBeforeDaysFromStart == null ? null : this.bestBeforeDaysFromStart;
        toRet.output = this.output == null ? null : this.output?.toJsonSerializedObject() ?? null;
        toRet.isHidden = this.isHidden;
        toRet.vesselFillColour = this.vesselFillColour;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "RecipeHeader.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "bestBeforeDaysFromStart", rule: (v: any) => v == null || v >= -2147483648 || "BestBeforeDaysFromStart is not greater than minimum allowed value: -2147483648"},
            {field: "bestBeforeDaysFromStart", rule: (v: any) => v == null || v <= 2147483647 || "BestBeforeDaysFromStart is above maximum allowed value: 2147483647"},
            {field: "output", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class RecipeProcessLineReadOnly extends ModelObject<RecipeProcessLineReadOnly> {

    /**
    * ProcessGroup to which this line belongs.
    */
    public recipeProcessGroupId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.recipeProcessGroupId = Guid.parseOrEmpty(jsonSerializedObject.recipeProcessGroupId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.recipeProcessGroupId = this.recipeProcessGroupId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "recipeProcessGroupId", rule: (v: any) => !v.isEmpty() || "RecipeProcessLineReadOnly.RecipeProcessGroupId cannot be empty"},
    ];
}


export class RecipeProcessLine extends ModelObject<RecipeProcessLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Sequence (for ordering Lines).
    */
    public sequence: number = 0;
    /**
    * Instruction
    * Not Nullable
    * Max Length : 100000
    */
    public instruction: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    public readOnly: RecipeProcessLineReadOnly = new RecipeProcessLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.sequence = jsonSerializedObject.sequence;
          this.instruction = jsonSerializedObject.instruction;
          this.notes = jsonSerializedObject.notes;
          this.readOnly = new RecipeProcessLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.sequence = this.sequence;
        toRet.instruction = this.instruction;
        toRet.notes = this.notes;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "RecipeProcessLine.Id cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "instruction", rule: (v: any) => v == null || v.length <= 100000 || "Instruction has too many characters (100000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class RecipeVesselRequirementReadOnly extends ModelObject<RecipeVesselRequirementReadOnly> {

    /**
    * ProcessGroup to which this line belongs.
    */
    public recipeProcessGroupId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.recipeProcessGroupId = Guid.parseOrEmpty(jsonSerializedObject.recipeProcessGroupId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.recipeProcessGroupId = this.recipeProcessGroupId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "recipeProcessGroupId", rule: (v: any) => !v.isEmpty() || "RecipeVesselRequirementReadOnly.RecipeProcessGroupId cannot be empty"},
    ];
}


export class RecipeVesselRequirement extends ModelObject<RecipeVesselRequirement> {

    public id: Guid = Guid.createEmpty();
    /**
    * Required Vessel Capability.
    */
    public vesselCapabilityId: Guid = Guid.createEmpty();
    /**
    * Required minimum capacity of Vessel in Litres
    * Not Negative
    */
    public capacityLitres: number = 0;
    public readOnly: RecipeVesselRequirementReadOnly = new RecipeVesselRequirementReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.vesselCapabilityId = Guid.parseOrEmpty(jsonSerializedObject.vesselCapabilityId);
          this.capacityLitres = jsonSerializedObject.capacityLitres;
          this.readOnly = new RecipeVesselRequirementReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.vesselCapabilityId = this.vesselCapabilityId.toString();
        toRet.capacityLitres = this.capacityLitres;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "RecipeVesselRequirement.Id cannot be empty"},
            {field: "vesselCapabilityId", rule: (v: any) => !v.isEmpty() || "RecipeVesselRequirement.VesselCapabilityId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class RecipeProcessGroupReadOnly extends ModelObject<RecipeProcessGroupReadOnly> {

    /**
    * Recipe to which this Process belongs.
    */
    public recipeId: Guid = Guid.createEmpty();
    /**
    * Sequence (for ordering Lines).
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.recipeId = Guid.parseOrEmpty(jsonSerializedObject.recipeId);
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.recipeId = this.recipeId.toString();
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "recipeId", rule: (v: any) => !v.isEmpty() || "RecipeProcessGroupReadOnly.RecipeId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class RecipeProcessGroup extends ModelObject<RecipeProcessGroup> {

    public id: Guid = Guid.createEmpty();
    /**
    * Name
    * Not Nullable
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Duration in days this process group is planned to take by default.
    * Not Negative
    */
    public plannedDurationDays: number = 0;
    public readOnly: RecipeProcessGroupReadOnly = new RecipeProcessGroupReadOnly();
    public lines: (RecipeProcessLine)[] = [];
    public vesselRequirements: (RecipeVesselRequirement)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.plannedDurationDays = jsonSerializedObject.plannedDurationDays;
          this.readOnly = new RecipeProcessGroupReadOnly(jsonSerializedObject.readOnly);
          this.lines = jsonSerializedObject.lines.map((v: any) => new RecipeProcessLine(v));
          this.vesselRequirements = jsonSerializedObject.vesselRequirements.map((v: any) => new RecipeVesselRequirement(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.plannedDurationDays = this.plannedDurationDays;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        toRet.vesselRequirements = this.vesselRequirements.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "RecipeProcessGroup.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "plannedDurationDays", rule: (v: any) => v == null || v >= 0 || "PlannedDurationDays is not greater than minimum allowed value: 0"},
            {field: "plannedDurationDays", rule: (v: any) => v == null || v <= 2147483647 || "PlannedDurationDays is above maximum allowed value: 2147483647"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as RecipeProcessLine[]).reduce((acc: Array<string>, curr: RecipeProcessLine) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "vesselRequirements", rule: (v: any) => !!v || "vesselRequirements is required"},
            {field: "vesselRequirements", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as RecipeVesselRequirement[]).reduce((acc: Array<string>, curr: RecipeVesselRequirement) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class Recipe extends ModelObjectWithDirtyCheck<Recipe> {

    public header: RecipeHeader = new RecipeHeader();
    public ingredients: (RecipeIngredientLine)[] = [];
    public processGroups: (RecipeProcessGroup)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new RecipeHeader(jsonSerializedObject.header);
          this.ingredients = jsonSerializedObject.ingredients.map((v: any) => new RecipeIngredientLine(v));
          this.processGroups = jsonSerializedObject.processGroups.map((v: any) => new RecipeProcessGroup(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.ingredients = this.ingredients.map(v => v.toJsonSerializedObject());
        toRet.processGroups = this.processGroups.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "ingredients", rule: (v: any) => !!v || "ingredients is required"},
            {field: "ingredients", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as RecipeIngredientLine[]).reduce((acc: Array<string>, curr: RecipeIngredientLine) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "processGroups", rule: (v: any) => !!v || "processGroups is required"},
            {field: "processGroups", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as RecipeProcessGroup[]).reduce((acc: Array<string>, curr: RecipeProcessGroup) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class RecipeService extends ServiceBase {

  public async getAllRecipes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Recipe)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.recipes.map((v: any) => new Recipe(v));;
    return this.makeJsonRequest("recipe/v1/getAllRecipes", req, false, extractor, opts);
  };

  public async getRecipe(recipeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Recipe> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    };
    const extractor = (response:any) => new Recipe(response.recipe);;
    return this.makeJsonRequest("recipe/v1/getRecipe", req, false, extractor, opts);
  };

  public async getRecipes(recipeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Recipe)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeIds:recipeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.recipes.map((v: any) => new Recipe(v));;
    return this.makeJsonRequest("recipe/v1/getRecipes", req, false, extractor, opts);
  };

  public async saveRecipe(recipe: Recipe, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipe:recipe.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v1/saveRecipe", req, true, extractor, opts);
  };

  public async setRecipeIsHidden(recipeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v1/setRecipeIsHidden", req, true, extractor, opts);
  };
}

export const recipeService = new RecipeService();